import React, { useState, useEffect } from 'react'
import { 
  Box, 
  Typography, 
  CircularProgress, 
  Alert, 
  Card, 
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Tabs,
  Tab
} from '@mui/material'
import { Download, ExpandMore, FileDownload, TrendingUp, TrendingDown } from '@mui/icons-material'
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  ResponsiveContainer,
  Cell
} from 'recharts'
import { apiService } from '../../services/api'
import debug from '../../utils/debug'
import {
  ReportShell, HeaderButton, FilterBar, KpiStrip, Icon, fmt,
} from '../report-shell'

const OrangeFTEAnalysis = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [analysisMode, setAnalysisMode] = useState('monthly') // 'yearly' or 'monthly'
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1) // Current month (1-12)
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())
  const [selectedRegion, setSelectedRegion] = useState(null)
  const [selectedDistrict, setSelectedDistrict] = useState(null)
  const [data, setData] = useState(null)
  const [activeTab, setActiveTab] = useState(0)

  const monthNames = [
    'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
    'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'
  ]

  // Get available regions and districts from data
  const availableRegions = React.useMemo(() => {
    if (!data?.regions) return []
    return data.regions.map(r => r.region_name).sort()
  }, [data])

  const availableDistricts = React.useMemo(() => {
    if (!data?.regions || !selectedRegion) return []
    const region = data.regions.find(r => r.region_name === selectedRegion)
    if (!region) return []
    return region.districts.map(d => d.district_name).sort()
  }, [data, selectedRegion])

  // Fetch Orange FTE analysis data
  const fetchData = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const monthParam = analysisMode === 'yearly' ? null : selectedMonth
      
      debug.log('OrangeFTEAnalysis', `Fetching data for mode: ${analysisMode}, month: ${monthParam}, year: ${selectedYear}`)
      
      const response = await apiService.getOrangeFTEAnalysis(
        9999, 
        monthParam, 
        selectedYear, 
        selectedRegion, 
        selectedDistrict
      )
      const analysisData = response.data
      
      console.log('OrangeFTEAnalysis - Data received:', analysisData)
      setData(analysisData)
      
    } catch (err) {
      debug.error('OrangeFTEAnalysis', 'Error fetching data:', err)
      setError(`Błąd pobierania danych analizy FTE: ${err.message}`)
      setData(null)
    } finally {
      setLoading(false)
    }
  }

  // Export FTE Excel
  const exportFTEExcel = async () => {
    try {
      debug.log('OrangeFTEAnalysis', 'Exporting FTE Excel')
      
      const monthParam = analysisMode === 'yearly' ? null : selectedMonth
      
      const response = await apiService.getOrangeFTEExport(
        monthParam, 
        selectedYear, 
        selectedRegion, 
        selectedDistrict
      )
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      
      // Generate filename
      const monthName = monthParam ? monthNames[monthParam - 1] : 'ROK'
      const filename = `Sprzedaz_vs_FTE_Orange_${monthName}_${selectedYear}.xlsx`
      
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
      
    } catch (err) {
      debug.error('OrangeFTEAnalysis', 'Error exporting Excel:', err)
      setError(`Błąd exportu Excel: ${err.message}`)
    }
  }

  // Load data when parameters change
  useEffect(() => {
    fetchData()
  }, [analysisMode, selectedMonth, selectedYear, selectedRegion, selectedDistrict])

  // Handle analysis mode change
  const handleAnalysisModeChange = (mode) => {
    setAnalysisMode(mode)
    if (mode === 'yearly') {
      setSelectedMonth(null)
    }
  }

  // Prepare chart data - Top 20 salons by sales per FTE
  const chartData = React.useMemo(() => {
    if (!data?.regions) return []
    
    const allSalons = []
    data.regions.forEach(region => {
      region.districts.forEach(district => {
        district.salons.forEach(salon => {
          allSalons.push({
            ...salon,
            region_name: region.region_name,
            district_name: district.district_name
          })
        })
      })
    })
    
    // Sort by sales_per_fte and take top 20
    return allSalons
      .filter(s => s.fte_count > 0) // Only show salons with FTE data
      .sort((a, b) => b.sales_per_fte - a.sales_per_fte)
      .slice(0, 20)
      .map((salon, index) => ({
        name: `${salon.salon_name.substring(0, 25)}...`,
        fullName: salon.salon_name,
        plant_code: salon.plant_code,
        sales_per_fte: salon.sales_per_fte,
        total_sales: salon.total_sales,
        fte_count: salon.fte_count,
        index: index + 1
      }))
  }, [data])

  // ─ FilterBar / KpiStrip config ──────────────────────────────
  const periods = [
    {
      type: 'segmented', key: 'analysisMode', label: 'Rodzaj analizy',
      value: analysisMode,
      options: [
        { value: 'monthly', label: 'Konkretny miesiąc' },
        { value: 'yearly',  label: 'Cały rok' },
      ],
      onChange: handleAnalysisModeChange,
    },
    {
      type: 'select', key: 'year', label: 'Rok',
      value: selectedYear, display: String(selectedYear),
      options: [new Date().getFullYear(), new Date().getFullYear() - 1, new Date().getFullYear() - 2]
        .map(y => ({ value: y, label: String(y) })),
      onChange: (v) => setSelectedYear(parseInt(v)),
      allowClear: false,
    },
    {
      type: 'select', key: 'month', label: 'Miesiąc',
      value: selectedMonth, display: selectedMonth ? monthNames[selectedMonth - 1] : '',
      options: monthNames.map((n, i) => ({ value: i + 1, label: n })),
      onChange: (v) => setSelectedMonth(parseInt(v)),
      allowClear: false,
      hideWhen: analysisMode !== 'monthly',
    },
  ]

  const filterFields = [
    {
      type: 'select', key: 'region', label: 'Region',
      value: selectedRegion,
      options: [{ value: null, label: 'Wszystkie regiony' },
        ...availableRegions.map(r => ({ value: r, label: r }))],
      onChange: (v) => { setSelectedRegion(v || null); setSelectedDistrict(null) },
    },
    {
      type: 'select', key: 'district', label: 'Dystrykt',
      value: selectedDistrict,
      options: [{ value: null, label: 'Wszystkie dystrykty' },
        ...availableDistricts.map(d => ({ value: d, label: d }))],
      onChange: (v) => setSelectedDistrict(v || null),
      hideWhen: !selectedRegion,
    },
  ]

  const kpiTiles = data ? [
    {
      key: 'sales', label: 'Łączna sprzedaż',
      value: fmt.int(data.total_sales || 0), unit: 'szt.',
    },
    {
      key: 'fte', label: 'Łączna liczba FTE',
      value: fmt.int(data.total_fte || 0),
    },
    {
      key: 'avg', label: 'Średnia sprzedaż / FTE',
      value: data.avg_sales_per_fte != null
        ? data.avg_sales_per_fte.toFixed(2).replace('.', ',')
        : '—',
      unit: 'szt./FTE',
    },
    {
      key: 'period', label: 'Okres analizy',
      value: data.analysis_period || '—',
    },
  ] : []

  return (
    <ReportShell
      title="Orange — Sprzedaż vs FTE"
      subtitle="Efektywność sprzedaży: ile urządzeń przypada na jednego pracownika (FTE)."
      breadcrumb={['KAM-SFA', 'Orange', 'Sprzedaż vs FTE']}
      accentColor="#ff7900"
      actions={
        <HeaderButton primary onClick={exportFTEExcel} disabled={loading || !data}>
          <Icon.download /> Eksport Excel
        </HeaderButton>
      }
      filterBar={
        <FilterBar
          periods={periods}
          filters={filterFields}
          onReset={() => { setSelectedRegion(null); setSelectedDistrict(null) }}
        />
      }
      kpiStrip={kpiTiles.length > 0 ? <KpiStrip tiles={kpiTiles} /> : null}
    >
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && !data && (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      )}

      {data && (
        <>
          {/* Hierarchical View */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                📍 Widok hierarchiczny: Regiony → Dystrykty → Salony
              </Typography>
              
              {data.regions.map((region) => (
                <Accordion key={region.region_name} sx={{ mb: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                      <Typography sx={{ width: '30%', flexShrink: 0 }}>
                        <strong>{region.region_name}</strong>
                      </Typography>
                      <Chip 
                        label={`Sprzedaż: ${region.total_sales.toLocaleString()} szt.`} 
                        color="primary" 
                        size="small"
                      />
                      <Chip 
                        label={`FTE: ${region.total_fte}`} 
                        color="success" 
                        size="small"
                      />
                      <Chip 
                        label={`Avg: ${region.avg_sales_per_fte.toFixed(2)} szt/FTE`} 
                        color="warning" 
                        size="small"
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {region.districts.map((district) => (
                      <Accordion key={district.district_name} sx={{ mb: 1 }}>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                            <Typography sx={{ width: '30%', flexShrink: 0 }}>
                              {district.district_name}
                            </Typography>
                            <Chip 
                              label={`Sprzedaż: ${district.total_sales.toLocaleString()} szt.`} 
                              color="secondary" 
                              size="small"
                            />
                            <Chip 
                              label={`FTE: ${district.total_fte}`} 
                              color="success" 
                              size="small"
                            />
                            <Chip 
                              label={`Avg: ${district.avg_sales_per_fte.toFixed(2)} szt/FTE`} 
                              color="warning" 
                              size="small"
                            />
                            <Typography variant="body2" color="text.secondary">
                              ({district.salon_count} salonów)
                            </Typography>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <TableContainer component={Paper}>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell><strong>Salon</strong></TableCell>
                                  <TableCell><strong>Plant Code</strong></TableCell>
                                  <TableCell><strong>Miasto</strong></TableCell>
                                  <TableCell align="right"><strong>Liczba FTE</strong></TableCell>
                                  <TableCell align="right"><strong>Sprzedaż (szt.)</strong></TableCell>
                                  <TableCell align="right"><strong>Sprzedaż na 1 FTE</strong></TableCell>
                                  <TableCell align="center"><strong>Status</strong></TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {district.salons.map((salon) => {
                                  const avgPerformance = salon.sales_per_fte >= data.avg_sales_per_fte
                                  return (
                                    <TableRow 
                                      key={salon.plant_code}
                                      sx={{ 
                                        backgroundColor: avgPerformance 
                                          ? 'rgba(76, 175, 80, 0.08)' 
                                          : 'rgba(244, 67, 54, 0.08)'
                                      }}
                                    >
                                      <TableCell>{salon.salon_name}</TableCell>
                                      <TableCell>{salon.plant_code}</TableCell>
                                      <TableCell>{salon.city}</TableCell>
                                      <TableCell align="right">
                                        <Chip label={salon.fte_count} size="small" />
                                      </TableCell>
                                      <TableCell align="right">
                                        <strong>{salon.total_sales.toLocaleString()}</strong>
                                      </TableCell>
                                      <TableCell align="right">
                                        <Chip 
                                          label={salon.sales_per_fte.toFixed(2)}
                                          color={avgPerformance ? 'success' : 'error'}
                                          size="small"
                                        />
                                      </TableCell>
                                      <TableCell align="center">
                                        {avgPerformance ? (
                                          <TrendingUp color="success" />
                                        ) : (
                                          <TrendingDown color="error" />
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  )
                                })}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </AccordionDetails>
                </Accordion>
              ))}
            </CardContent>
          </Card>

          {/* Charts Section */}
          <Box sx={{ mb: 3 }}>
            <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
              <Tab label="🏆 TOP 20 Salonów (Sprzedaż na FTE)" />
            </Tabs>

            <Box sx={{ mt: 2 }}>
              {activeTab === 0 && (
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      TOP 20 Salonów według sprzedaży na 1 FTE
                    </Typography>
                    
                    <Alert severity="info" sx={{ mb: 2 }}>
                      💡 Wykres pokazuje salony o najwyższej efektywności - najwięcej sprzedanych urządzeń na 1 pracownika
                    </Alert>
                    
                    <Box sx={{ height: 600 }}>
                      {chartData.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart 
                            data={chartData} 
                            margin={{ top: 20, right: 50, left: 20, bottom: 150 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                              dataKey="name" 
                              angle={-45}
                              textAnchor="end"
                              height={120}
                              interval={0}
                              tick={{ fontSize: 10 }}
                            />
                            <YAxis label={{ value: 'Sprzedaż na FTE', angle: -90, position: 'insideLeft' }} />
                            <RechartsTooltip 
                              formatter={(value, name, props) => {
                                const data = props.payload
                                return [
                                  `${value} szt/FTE`,
                                  `Sprzedaż na FTE`
                                ]
                              }}
                              labelFormatter={(label, payload) => {
                                const data = payload?.[0]?.payload
                                return data ? (
                                  <div>
                                    <div><strong>{data.fullName}</strong></div>
                                    <div>Plant Code: {data.plant_code}</div>
                                    <div>Sprzedaż: {data.total_sales} szt.</div>
                                    <div>FTE: {data.fte_count}</div>
                                  </div>
                                ) : label
                              }}
                            />
                            <Bar dataKey="sales_per_fte" fill="#4caf50">
                              {chartData.map((entry, index) => (
                                <Cell 
                                  key={`cell-${index}`} 
                                  fill={index < 5 ? '#4caf50' : index < 10 ? '#ff9800' : '#2196f3'} 
                                />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <Alert severity="warning">
                          Brak danych do wyświetlenia wykresu
                        </Alert>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              )}
            </Box>
          </Box>

          {/* Legend */}
          <Alert severity="info">
            💡 <strong>Interpretacja:</strong> Zielone wiersze = efektywność powyżej średniej ({data.avg_sales_per_fte?.toFixed(2)} szt/FTE) |
            Czerwone wiersze = efektywność poniżej średniej
          </Alert>
        </>
      )}
    </ReportShell>
  )
}

export default OrangeFTEAnalysis










